import type { BessUnitSnapshot } from "@/data/types";
import { isIdlePower } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { scada } from "../scadaColors";

/**
 * BESS — rendered as an actual battery so the operator never has to
 * remember "the box on the right is a battery". Two terminal posts on
 * top, a stacked-cell body with a vertical SOC electrolyte fill, and a
 * thin plinth grounding it to the bus.
 *
 * Visual signals (in order of operator priority):
 *   1. SOC fill height — the obvious "fuel gauge".
 *   2. SOC % readout — centred inside the body.
 *   3. Inner glow + bolt icon — animated only when charging/discharging.
 *   4. Top LED — green pulsing = healthy, amber = fault, red = offline.
 *   5. Per-unit label below: name, state, capacity.
 */
export function BessCabinet({
  x, y, w = 110, h = 130, unit, capacity_kWh, density = "rich",
}: {
  x: number; y: number; w?: number; h?: number;
  unit: BessUnitSnapshot;
  family: string;          // kept for API parity; no longer rendered
  capacity_kWh: number;
  /** 'lean' (compact) drops the inner-glow halo + the pulsing bolt/arrow
   *  animate, keeping the static glyph + electrolyte + LED alive cues. */
  density?: "lean" | "rich";
}) {
  const t = useT();
  const online = unit.online;
  const fault = unit.fault_count > 0;
  const idle = isIdlePower(unit.active_W);
  const charging = !idle && unit.active_W > 0;
  const discharging = !idle && unit.active_W < 0;

  // Accent — primary tint for terminals, frame, and inner glow.
  const accent = !online ? scada.critical()
    : fault       ? scada.warn()
    : charging    ? scada.charge()
    : discharging ? scada.discharge()
    : scada.idle();
  const ledColor = !online ? scada.critical() : fault ? scada.warn() : scada.ok();

  // Battery electrolyte — fuel-gauge tone.
  const soc = Math.max(0, Math.min(100, unit.soc_pct));
  const socTone =
    soc < 15 ? scada.critical()
    : soc < 30 ? scada.warn()
    : soc > 95 ? scada.warn()
    : scada.charge();

  // Geometry. The body is a rounded rectangle with two terminal nubs on
  // top — drawn relative to (x, y, w, h).
  const termH = 8;             // height of the terminal nubs
  const termW = Math.max(14, w * 0.18);
  const bodyY = y + termH;
  const bodyW = w;
  const plinthH = 8;
  const bodyH = h - termH - plinthH;

  // Cell-stack divider count scales with body height.
  const cellCount = bodyH < 90 ? 3 : bodyH < 140 ? 4 : 5;

  // Unique id ramp for the gradients (avoid collisions across instances).
  const idSuffix = `${Math.round(x)}-${Math.round(y)}`;
  const gid    = `bess-body-${idSuffix}`;
  const fillId = `bess-fill-${idSuffix}`;
  const glowId = `bess-glow-${idSuffix}`;

  // Fill rectangle anchored at the BOTTOM of the body — grows upward.
  const innerPad = 5;
  const innerX = x + innerPad;
  const innerY = bodyY + innerPad;
  const innerW = bodyW - innerPad * 2;
  const innerH = bodyH - innerPad * 2;
  const fillH = innerH * (soc / 100);
  const fillY = innerY + innerH - fillH;

  // Below-cabinet label content. State words are i18n'd (status.*); the kW
  // value + unit stay raw.
  const stateLabel =
    !online ? t("status.offline")
    : charging ? `+${(unit.active_W / 1000).toFixed(1)} kW · ${t("status.charging")}`
    : discharging ? `${(unit.active_W / 1000).toFixed(1)} kW · ${t("status.discharging")}`
    : t("status.idle");

  return (
    <g style={{ cursor: "pointer" }}>
      <defs>
        {/* Battery housing — silver/metal gradient. */}
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor="var(--scada-surface-hi)" />
          <stop offset="55%" stopColor="var(--scada-surface-mid)" />
          <stop offset="100%" stopColor="var(--scada-surface-lo)" />
        </linearGradient>
        {/* Electrolyte fill — vertical gradient so the bottom reads denser. */}
        <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor={socTone} stopOpacity={0.55} />
          <stop offset="100%" stopColor={socTone} stopOpacity={0.95} />
        </linearGradient>
        {/* Soft inner halo for the active state. */}
        <radialGradient id={glowId} cx="50%" cy="50%" r="50%">
          <stop offset="0%"  stopColor={accent} stopOpacity={0.35} />
          <stop offset="100%" stopColor={accent} stopOpacity={0} />
        </radialGradient>
      </defs>

      {/* Ground shadow */}
      <ellipse cx={x + w / 2} cy={y + h + 4} rx={w * 0.42} ry={3} fill="var(--scada-shadow)" />

      {/* Plinth — base. */}
      <rect x={x - 2} y={y + h - plinthH} width={w + 4} height={plinthH} rx={1.6}
        fill="var(--ems-rule-strong)"
        stroke="var(--ems-border)" strokeWidth={0.5} />

      {/* Terminal posts — two little caps on top. */}
      <rect x={x + w * 0.18 - termW / 2} y={y} width={termW} height={termH}
        rx={1.4}
        fill="var(--scada-surface-shade)"
        stroke={accent} strokeOpacity={0.7} strokeWidth={0.9} />
      <rect x={x + w * 0.82 - termW / 2} y={y} width={termW} height={termH}
        rx={1.4}
        fill="var(--scada-surface-shade)"
        stroke={accent} strokeOpacity={0.7} strokeWidth={0.9} />

      {/* Polarity ticks on terminals: − on the left, + on the right */}
      <line
        x1={x + w * 0.18 - 3} y1={y + termH / 2}
        x2={x + w * 0.18 + 3} y2={y + termH / 2}
        stroke="var(--text)" strokeWidth={1} />
      <g stroke="var(--text)" strokeWidth={1}>
        <line x1={x + w * 0.82 - 3} y1={y + termH / 2} x2={x + w * 0.82 + 3} y2={y + termH / 2} />
        <line x1={x + w * 0.82} y1={y + termH / 2 - 3} x2={x + w * 0.82} y2={y + termH / 2 + 3} />
      </g>

      {/* Body — rounded battery housing. */}
      <rect x={x} y={bodyY} width={bodyW} height={bodyH} rx={6}
        fill={`url(#${gid})`}
        stroke={accent} strokeWidth={1.6} />

      {/* Inner well — slightly inset, lighter so the electrolyte sits inside. */}
      <rect x={innerX} y={innerY} width={innerW} height={innerH} rx={3}
        fill="var(--ems-rule)" stroke="var(--ems-border)" strokeWidth={0.6} />

      {/* Electrolyte fill — anchored at bottom, grows with SOC. */}
      {fillH > 0 && (
        <rect x={innerX} y={fillY} width={innerW} height={fillH} rx={2}
          fill={`url(#${fillId})`}>
          {online && (charging || discharging) && (
            <animate attributeName="opacity" values="1;0.78;1" dur="2s" repeatCount="indefinite" />
          )}
        </rect>
      )}

      {/* Cell divider lines inside the well — 3-5 horizontal slices, evenly
          spaced. They sit on top of the electrolyte to read as physical
          cell boundaries, not just decoration. */}
      <g pointerEvents="none">
        {Array.from({ length: cellCount - 1 }).map((_, i) => {
          const cellY = innerY + (innerH / cellCount) * (i + 1);
          return (
            <line key={i}
              x1={innerX + 1} y1={cellY}
              x2={innerX + innerW - 1} y2={cellY}
              stroke="var(--ems-border)" strokeOpacity={0.7} strokeWidth={0.5}
            />
          );
        })}
      </g>

      {/* Animated charge/discharge inner glow — only when there's actual
          activity. Suppressed in lean (compact) density. */}
      {density === "rich" && (charging || discharging) && online && !fault && (
        <rect x={x + 1.5} y={bodyY + 1.5} width={bodyW - 3} height={bodyH - 3} rx={5}
          fill={`url(#${glowId})`} pointerEvents="none">
          <animate attributeName="opacity" values="0.85;0.45;0.85" dur="2.4s" repeatCount="indefinite" />
        </rect>
      )}

      {/* Lightning bolt overlay (charge) or arrow-up (discharge), centred. */}
      {online && !fault && (charging || discharging) && (() => {
        const cx = x + w / 2;
        const cy = innerY + innerH / 2;
        const s = Math.min(bodyW, bodyH) * 0.20;
        if (charging) {
          return (
            <path
              d={`M${cx - s * 0.30},${cy - s * 0.95}
                  L${cx + s * 0.10},${cy - s * 0.10}
                  L${cx - s * 0.05},${cy - s * 0.10}
                  L${cx + s * 0.30},${cy + s * 0.95}
                  L${cx - s * 0.10},${cy + s * 0.10}
                  L${cx + s * 0.05},${cy + s * 0.10} Z`}
              fill="var(--surface)"
              stroke={accent}
              strokeWidth={1.2}
              opacity={0.96}
            >
              {density === "rich" && (
                <animate attributeName="opacity" values="0.95;0.55;0.95" dur="1.4s" repeatCount="indefinite" />
              )}
            </path>
          );
        }
        // Discharge: outward arrow pointing UP. The BESS terminals are at
        // the top of the cabinet and power leaves via them toward the bus
        // — a down arrow here would contradict the actual flow direction
        // (and the marching dashes on the trunk above).
        return (
          <path
            d={`M${cx - s * 0.6},${cy + s * 0.4}
                L${cx + s * 0.6},${cy + s * 0.4}
                L${cx + s * 0.6},${cy - s * 0.05}
                L${cx + s * 0.9},${cy - s * 0.05}
                L${cx},${cy - s * 0.85}
                L${cx - s * 0.9},${cy - s * 0.05}
                L${cx - s * 0.6},${cy - s * 0.05} Z`}
            fill="var(--surface)"
            stroke={accent}
            strokeWidth={1.2}
            opacity={0.96}
          >
            {density === "rich" && (
              <animate attributeName="opacity" values="0.95;0.55;0.95" dur="1.4s" repeatCount="indefinite" />
            )}
          </path>
        );
      })()}

      {/* SOC percentage — centred inside the body */}
      <text
        x={x + w / 2} y={bodyY + bodyH / 2 + 5}
        textAnchor="middle"
        fontSize={Math.min(20, bodyH * 0.18)}
        fontWeight={800}
        fill="var(--text)"
        className="num"
        style={{ pointerEvents: "none" }}
      >
        {Math.round(soc)}%
      </text>
      <text
        x={x + w / 2} y={bodyY + bodyH / 2 + 19}
        textAnchor="middle"
        fontSize={8} fontWeight={600}
        fill="var(--text-muted)"
        letterSpacing={1.4}
        style={{ pointerEvents: "none" }}
      >
        SOC
      </text>

      {/* LED — small status dot near the right terminal */}
      <circle cx={x + w * 0.82} cy={y - 4} r={2.8}
        fill={ledColor}
        stroke="var(--surface)" strokeWidth={0.7}>
        {online && (
          <animate attributeName="opacity" values="1;0.4;1" dur="1.6s" repeatCount="indefinite" />
        )}
      </circle>

      {/* SINGLE clean label below the battery */}
      <text x={x + w / 2} y={y + h + 18}
        textAnchor="middle"
        fontSize={10.5} fontWeight={700}
        fill="var(--text)">
        {unit.name}
      </text>
      <text x={x + w / 2} y={y + h + 30}
        textAnchor="middle"
        fontSize={9} fontWeight={500}
        fill={online ? (charging ? scada.charge() : discharging ? scada.discharge() : "var(--text-muted)") : scada.critical()}>
        {stateLabel}
      </text>
      <text x={x + w / 2} y={y + h + 42}
        textAnchor="middle"
        fontSize={8.5}
        fill="var(--text-faint)">
        {capacity_kWh.toFixed(0)} kWh
      </text>

      {/* Offline overlay */}
      {!online && (
        <g pointerEvents="none">
          <rect x={x} y={bodyY} width={bodyW} height={bodyH} rx={6}
            fill={scada.critical()} fillOpacity={0.1}
            stroke={scada.critical()} strokeWidth={1.4}
            strokeDasharray="4 3">
            <animate attributeName="stroke-dashoffset" values="0;-7" dur="1s" repeatCount="indefinite" />
          </rect>
        </g>
      )}
    </g>
  );
}
